import type {IPaginationResponse} from "@sitemap/apiendponts.ts";
import * as React from "react";
import {useScreenSize} from "@hooks/useScreenSize.tsx";
import {IconRefresh} from "@tabler/icons-react";
import {
	ActionIcon,
	Box,
	Button,
	Group,
	Pagination,
	Popover,
	Stack,
	Text,
	TextInput,
	Tooltip,
} from "@mantine/core";

export function PaginationComponent(props: {
	pagination: IPaginationResponse;
	setPagination?: React.Dispatch<React.SetStateAction<IPaginationResponse>>;
	loading?: boolean;
	sx?: any;
	options?: {
		showResultPerPage?: boolean;
	};
}) {
	const {pagination, loading, setPagination} = props;
	const {isMobile} = useScreenSize();
	const [popoverOpened, setPopoverOpened] = React.useState(false);
	const [currentPage, setCurrentPage] = React.useState<number>(pagination.page ?? 1);

	React.useEffect(() => {
		setCurrentPage(pagination.page ?? 1);
	}, [pagination.page]);

	const applyPage = (pageToApply?: number) => {
		if (setPagination == null) return;
		const target = pageToApply ?? currentPage;
		if (isNaN(target) || target < 1) return;
		setPopoverOpened(false);
		if (target === pagination.page) {
			setPagination((prev) => ({...prev, ignoreCache: true}));
			return;
		}
		setPagination((prev) => ({...prev, page: target}));
	};

	const totalPages = Math.max(pagination.totalPages ?? 1, 1);

	return (
		<Group
			justify="space-between"
			align="center"
			wrap="wrap"
			gap="xs"
			py="xs"
			px="xs"
			style={{width: "100%"}}
		>
			<Group gap="xs" align="center">
				<div>
					<Text size="sm" fw={600}>
						Resultados: {pagination.results ?? 0}
					</Text>
					{(props.options?.showResultPerPage ?? true) && (
						<Text size="xs" c="dimmed">
							Por página: {pagination.pageLimit ?? 10}
						</Text>
					)}
				</div>

				<Tooltip label="Atualizar dados (recarregar sem cache)" position="top" withArrow>
					<ActionIcon
						variant="subtle"
						size="md"
						disabled={loading}
						loading={loading}
						onClick={() => {
							if (setPagination) {
								setPagination((prev) => ({...prev, ignoreCache: true}));
							}
						}}
					>
						<IconRefresh size={18} />
					</ActionIcon>
				</Tooltip>
			</Group>

			<Popover
				opened={popoverOpened}
				onChange={setPopoverOpened}
				position="bottom"
				withArrow
				shadow="md"
			>
				<Popover.Target>
					<Box
						onContextMenu={(e) => {
							e.preventDefault();
							setPopoverOpened((prev) => !prev);
						}}
					>
						<Pagination
							total={totalPages}
							value={pagination.page ?? 1}
							onChange={(p) => {
								if (setPagination == null) return;
								if (p === pagination.page) {
									setPagination((prev) => ({...prev, ignoreCache: true}));
									return;
								}
								setPagination((prev) => ({...prev, page: p}));
							}}
							disabled={loading || setPagination == null}
							siblings={isMobile ? 0 : 1}
							boundaries={1}
							size={isMobile ? "xs" : "sm"}
							color="primary"
							radius="sm"
						/>
					</Box>
				</Popover.Target>

				<Popover.Dropdown>
					<Stack gap="xs" p={4} style={{minWidth: 160}}>
						<Text size="xs" fw={600}>
							Ir para página (1 - {totalPages})
						</Text>
						<Group gap="xs">
							<TextInput
								type="number"
								size="xs"
								style={{width: 80}}
								value={currentPage}
								min={1}
								max={totalPages}
								onChange={(e) => setCurrentPage(parseInt(e.target.value) || 1)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										applyPage();
									}
								}}
							/>
							<Button size="xs" color="primary" onClick={() => applyPage()}>
								Ir
							</Button>
						</Group>
					</Stack>
				</Popover.Dropdown>
			</Popover>
		</Group>
	);
}